import { Extension, Mark, Node, mergeAttributes } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { colorTokenVar, safeColor } from "../tokens";

const TEXT_ALIGNS = ["left", "center", "right", "justify"] as const;
const CELL_ALIGNS = ["left", "center", "right"] as const;

/** An alignment from the document or pasted HTML, if it is one of `allowed` (never raw into a style). */
function alignOf(value: unknown, allowed: readonly string[]): string | null {
  const v = typeof value === "string" ? value.trim().toLowerCase() : "";
  return allowed.includes(v) ? v : null;
}

/** Links open only web and mail addresses (`javascript:`, `data:` and the like are never links). */
export function safeHref(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const href = value.trim();
  // eslint-disable-next-line no-control-regex
  return /^(https?:\/\/|mailto:)/i.test(href) && !/[\u0000-\u001f\u007f]/.test(href) ? href : null;
}

/**
 * `doc = title block+` (document-model.md §4.2) is what clients write; the editor reads more
 * leniently. A document whose first block is not the title — no title, a block inserted before it,
 * two titles from two clients creating the note at once — must open and stay exactly as it is
 * ("never delete or rewrite what we do not expect"). With the strict expression ProseMirror fitted
 * such a document on the first edit: it inserted a new empty title and turned the real one into a
 * paragraph (data bug 2026-10-26). A new empty document still starts with a title (the first
 * alternative).
 *
 * The same set of documents is written `(title | block) (block | title)*` because ProseMirror takes
 * the first alternative as the default block type at each position: with `(title | block)+` every
 * block after the first defaulted to a title, so Enter at the end of a paragraph (and leaving a code
 * block, a node selection, …) created an empty title, which the title repair then deleted — Enter
 * did nothing (bug 2026-11-05). Now only the start of the note defaults to a title.
 */
export const DocumentNode = Node.create({
  name: "doc",
  topNode: true,
  content: "(title | block) (block | title)*",
});

export const Title = Node.create({
  name: "title",
  content: "inline*",
  marks: "_",
  defining: true,
  parseHTML: () => [{ tag: "h1[data-type=title]" }],
  renderHTML: ({ HTMLAttributes }) => ["h1", mergeAttributes(HTMLAttributes, { "data-type": "title", class: "ds-title" }), 0],
  // Enter in the title (no split, move to the text, default name): schema/title.ts.
});

/** Optional `textAlign` on paragraphs and headings (null = natural alignment). */
export const TextAlign = Extension.create({
  name: "dsTextAlign",
  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading"],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (el) => alignOf((el as HTMLElement).style.textAlign, TEXT_ALIGNS),
            renderHTML: (attrs) => {
              const align = alignOf(attrs.textAlign, TEXT_ALIGNS);
              return align ? { style: `text-align: ${align}` } : {};
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setTextAlign:
        (align: "left" | "center" | "right" | "justify" | null) =>
        ({ commands }) =>
          ["paragraph", "heading"].map((t) => commands.updateAttributes(t, { textAlign: align })).some(Boolean),
    };
  },
});

/**
 * Column alignment of a table (document-model.md §4.2): the cell attribute `align` on
 * `tableHeader`/`tableCell`, as Markdown writes it (`:---`, `:---:`, `---:`). No attribute =
 * default alignment. Agents and iOS write the same attribute.
 */
export const TableCellAlign = Extension.create({
  name: "dsTableAlign",
  addGlobalAttributes() {
    return [
      {
        types: ["tableHeader", "tableCell"],
        attributes: {
          align: {
            default: null,
            parseHTML: (el) => alignOf((el as HTMLElement).style.textAlign || (el as HTMLElement).getAttribute("align"), CELL_ALIGNS),
            renderHTML: (attrs) => {
              const align = alignOf(attrs.align, CELL_ALIGNS);
              return align ? { style: `text-align: ${align}` } : {};
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setColumnAlign:
        (align: "left" | "center" | "right" | null) =>
        ({ commands }) =>
          ["tableHeader", "tableCell"].map((t) => commands.updateAttributes(t, { align })).some(Boolean),
    };
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    dsTextAlign: { setTextAlign: (align: "left" | "center" | "right" | "justify" | null) => ReturnType };
    dsTableAlign: { setColumnAlign: (align: "left" | "center" | "right" | null) => ReturnType };
    dsHighlight: { toggleHighlight: (color?: string) => ReturnType; unsetHighlight: () => ReturnType };
    dsTextColor: { setTextColor: (color: string) => ReturnType; unsetTextColor: () => ReturnType };
    dsAgentEdit: { acceptAgentEdits: () => ReturnType };
    dsHorizontalRule: { setRuleStyle: (style: "line" | "dots" | "wave") => ReturnType };
  }
}

/** `link` keeps only `href` (contract), unlike TipTap's default target/rel/class. */
export const DsLink = Link.extend({
  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (el: HTMLElement) => safeHref(el.getAttribute("href")),
      },
    };
  },
  // A pasted or loaded <a> with any other scheme is plain text.
  parseHTML() {
    return [{ tag: "a[href]", getAttrs: (el) => (safeHref((el as HTMLElement).getAttribute("href")) ? null : false) }];
  },
  renderHTML({ HTMLAttributes }) {
    // The document may carry any href (other clients, agents): only a safe one is rendered.
    const href = safeHref(HTMLAttributes.href);
    return ["a", mergeAttributes({ ...HTMLAttributes, href }, { rel: "noopener noreferrer nofollow", target: "_blank" }), 0];
  },
}).configure({ openOnClick: false, autolink: true, linkOnPaste: true, isAllowedUri: (url) => safeHref(url) !== null });

/** `highlight` mark with a colour token (`highlight.yellow`, …). */
export const Highlight = Mark.create({
  name: "highlight",
  addAttributes() {
    return {
      color: {
        default: "highlight.yellow",
        parseHTML: (el) => safeColor(el.getAttribute("data-color")) ?? "highlight.yellow",
        renderHTML: (attrs) => ({ "data-color": attrs.color, style: `--ds-mark-bg: ${colorTokenVar(attrs.color)}` }),
      },
    };
  },
  parseHTML: () => [{ tag: "mark" }],
  renderHTML: ({ HTMLAttributes }) => ["mark", mergeAttributes(HTMLAttributes, { class: "ds-highlight" }), 0],
  addCommands() {
    return {
      toggleHighlight:
        (color = "highlight.yellow") =>
        ({ commands }) =>
          commands.toggleMark(this.name, { color }),
      unsetHighlight:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
  addKeyboardShortcuts() {
    return { "Mod-Shift-h": () => this.editor.commands.toggleHighlight() };
  },
});

/** `textColor` mark with a colour token. */
export const TextColor = Mark.create({
  name: "textColor",
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (el) => safeColor(el.getAttribute("data-text-color")),
        renderHTML: (attrs) => ({ "data-text-color": attrs.color, style: `color: ${colorTokenVar(attrs.color)}` }),
      },
    };
  },
  parseHTML: () => [{ tag: "span[data-text-color]" }],
  renderHTML: ({ HTMLAttributes }) => ["span", HTMLAttributes, 0],
  addCommands() {
    return {
      setTextColor:
        (color) =>
        ({ commands }) =>
          commands.setMark(this.name, { color }),
      unsetTextColor:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/** `agentEdit` — highlights text written by an AI agent until the user accepts it (D-10). */
export const AgentEdit = Mark.create({
  name: "agentEdit",
  inclusive: false,
  addAttributes() {
    return {
      agentId: { default: null, parseHTML: (el) => el.getAttribute("data-agent-id") },
      at: { default: null, parseHTML: (el) => el.getAttribute("data-agent-at") },
    };
  },
  parseHTML: () => [{ tag: "span[data-agent-id]" }],
  renderHTML: ({ mark }) => [
    "span",
    { class: "ds-agent-edit", "data-agent-id": mark.attrs.agentId, "data-agent-at": mark.attrs.at, title: "Изменено агентом" },
    0,
  ],
  addCommands() {
    return {
      acceptAgentEdits:
        () =>
        ({ tr, state, dispatch }) => {
          const type = state.schema.marks.agentEdit!;
          const { from, to, empty } = state.selection;
          if (dispatch) {
            if (empty) tr.removeMark(0, state.doc.content.size, type);
            else tr.removeMark(from, to, type);
          }
          return true;
        },
    };
  },
});

/** `horizontalRule` with `style: line | dots | wave`. */
export const DsHorizontalRule = HorizontalRule.extend({
  addAttributes() {
    return {
      style: {
        default: "line",
        parseHTML: (el) => el.getAttribute("data-style") ?? "line",
        renderHTML: (attrs) => ({ "data-style": attrs.style }),
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { class: "ds-hr", "data-type": "horizontalRule" }), ["hr"]];
  },
  parseHTML() {
    return [{ tag: "hr" }, { tag: "div[data-type=horizontalRule]" }];
  },
  addCommands() {
    return {
      ...this.parent?.(),
      setRuleStyle:
        (style) =>
        ({ commands }) =>
          commands.updateAttributes(this.name, { style }),
    };
  },
});
